/**
 * A short label for the period a document covers, from the ISO dates
 * classification read ("2026-04-01" … "2026-06-30" → "Q2 2026").
 *
 * Only the shapes that can be named without guessing get a name: a calendar
 * quarter, a twelve-month year (named by its end, so a June year end reads
 * "FY ending Jun 2026"), else the month the period ends in. No dates → null.
 */
const MONTHS = [
	"Jan", "Feb", "Mar", "Apr", "May", "Jun",
	"Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

interface YearMonth {
	year: number;
	month: number; // 0-11
}

const parse = (iso: string | null | undefined): YearMonth | null => {
	const m = iso?.match(/^(\d{4})-(\d{2})/);
	if (!m) return null;
	const month = Number(m[2]) - 1;
	if (month < 0 || month > 11) return null;
	return { year: Number(m[1]), month };
};

const monthsBetween = (a: YearMonth, b: YearMonth) =>
	(b.year - a.year) * 12 + (b.month - a.month) + 1;

export function formatPeriod(
	start: string | null | undefined,
	end: string | null | undefined,
	fallbackDate?: string | null,
): string | null {
	const e = parse(end);
	const s = parse(start);
	if (e && s) {
		const span = monthsBetween(s, e);
		if (span === 3 && e.month % 3 === 2) {
			return `Q${Math.floor(e.month / 3) + 1} ${e.year}`;
		}
		if (span === 12) {
			return e.month === 11
				? `FY ${e.year}`
				: `FY ending ${MONTHS[e.month]} ${e.year}`;
		}
	}
	const d = e ?? parse(fallbackDate);
	return d ? `${MONTHS[d.month]} ${d.year}` : null;
}

/**
 * A calendar date read off a document ("2026-01-20" → "Jan 20, 2026"). Parsed
 * as text, not through `Date`, so no time zone can move it a day.
 */
export function formatIsoDate(iso: string | null | undefined): string | null {
	const m = iso?.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (!m) return null;
	const month = Number(m[2]) - 1;
	if (month < 0 || month > 11) return null;
	return `${MONTHS[month]} ${Number(m[3])}, ${m[1]}`;
}
